import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ExternalLink, Clock } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

export const Contact = () => {
  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 lg:py-40 overflow-hidden bg-[#0a0908]">
        <motion.div 
          className="max-w-7xl mx-auto px-6 sm:px-12"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Contact
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#fdfbf7] mb-8 leading-tight max-w-4xl"
          >
            Parlons de{' '}
            <span className="text-[#C5A059]">votre projet</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base sm:text-lg text-[#a39c93] max-w-2xl leading-relaxed font-body"
          >
            Vous avez un projet de construction, de rénovation ou d'aménagement ? 
            Posons ensemble les bases d'un lieu cohérent, performant et durable.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Info */}
      <section className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-8">
                Nos coordonnées
              </h2>
              
              <div className="space-y-8">
                {/* Téléphone */}
                <a 
                  href="tel:+33609136334"
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 bg-[#1c1a17] border border-[#2a2622] flex items-center justify-center group-hover:border-[#C5A059]/50 transition-colors">
                    <Phone size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#a39c93] mb-1">Téléphone</p>
                    <p className="text-[#fdfbf7] text-lg font-body group-hover:text-[#C5A059] transition-colors">
                      06 09 13 63 34
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href="mailto:contact@rozier-menuiserie.fr"
                  data-testid="contact-email"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 bg-[#1c1a17] border border-[#2a2622] flex items-center justify-center group-hover:border-[#C5A059]/50 transition-colors">
                    <Mail size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#a39c93] mb-1">Email</p>
                    <p className="text-[#fdfbf7] text-lg font-body group-hover:text-[#C5A059] transition-colors">
                      contact@rozier-menuiserie.fr
                    </p>
                  </div>
                </a>

                {/* Zone d'intervention */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c1a17] border border-[#2a2622] flex items-center justify-center">
                    <MapPin size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#a39c93] mb-1">Zone d'intervention</p>
                    <p className="text-[#fdfbf7] text-lg font-body">
                      France entière
                    </p>
                  </div>
                </div>

                {/* Horaires */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1c1a17] border border-[#2a2622] flex items-center justify-center">
                    <Clock size={20} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#a39c93] mb-1">Disponibilité</p>
                    <p className="text-[#fdfbf7] text-lg font-body">
                      Lundi - Vendredi : 8h - 18h
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Rozier Concept Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-[#1c1a17] border border-[#C5A059]/30 p-8 sm:p-10 h-full">
                <h3 className="font-heading text-2xl text-[#fdfbf7] mb-4">
                  Découvrez Rozier Concept
                </h3>
                <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                  Rozier Menuiserie est une branche de Rozier Concept, notre approche globale 
                  qui englobe le Feng Shui structurel, la géobiologie appliquée, le paysagisme 
                  et la conception bioclimatique.
                </p>
                <p className="text-[#a39c93] leading-relaxed mb-8 font-body">
                  Découvrez comment nous structurons les lieux du visible à l'invisible pour créer 
                  des espaces harmonieux et équilibrés.
                </p>
                <a
                  href="https://rozier-concept.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-rozier-concept-link"
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0a0908] px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
                >
                  Visiter Rozier Concept
                  <ExternalLink size={14} />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Reminder */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              Nos expertises
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7]">
              Nous intervenons sur
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-[#1c1a17] border border-[#2a2622] p-8 text-center hover:border-[#C5A059]/50 transition-colors">
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-2">Isolation</h3>
              <p className="text-[#a39c93] text-sm font-body">Thermique par l'extérieur (ITE)</p>
            </div>
            <div className="bg-[#1c1a17] border border-[#2a2622] p-8 text-center hover:border-[#C5A059]/50 transition-colors">
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-2">Ossature Bois</h3>
              <p className="text-[#a39c93] text-sm font-body">Construction, extension, aménagement</p>
            </div>
            <div className="bg-[#1c1a17] border border-[#2a2622] p-8 text-center hover:border-[#C5A059]/50 transition-colors">
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-2">Menuiserie</h3>
              <p className="text-[#a39c93] text-sm font-body">Intérieure et extérieure</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-24 sm:py-32 bg-[#1c1a17]">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-px bg-[#C5A059] mx-auto mb-8"></div>
            <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl font-light text-[#fdfbf7] italic leading-relaxed mb-8">
              "L'invisible structure le visible"
            </blockquote>
            <p className="text-[#C5A059] uppercase tracking-[0.2em] text-sm">
              Rozier Menuiserie
            </p>
            <div className="w-16 h-px bg-[#C5A059] mx-auto mt-8"></div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
